import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export interface AdminPageHeaderProps {
  /** Page title, rendered as h1 */
  title: string;
  /** Optional subtitle under the title */
  subtitle?: string;
  /** Optional right-aligned action slot, e.g. a "Create" Button */
  action?: ReactNode;
  /** Optional back-link destination, e.g. "/admin/users" */
  backTo?: string;
  /** Label for the back link (default "Back") */
  backLabel?: string;
  /** Optional content rendered to the left of the title/subtitle block, e.g. a
   * team/agency logo avatar. Vertically centered alongside the title. Absent by
   * default — no layout shift for existing callers. */
  leading?: ReactNode;
  /** Additional classes appended to the base styles on the outer wrapper. Note: not guaranteed to override conflicting base utilities (cn is a concat, not tailwind-merge) — prefer additive classes. */
  className?: string;
  /** Optional browser tab title (via useDocumentTitle). Absent by default — no
   * behavior change for existing callers; falls back to the app default title. */
  documentTitle?: string;
}

/**
 * AdminPageHeader - Plain h1 + subtitle admin page header, matching the
 * hand-rolled pattern at the top of every admin list page (Users.tsx,
 * Teams.tsx, Agencies.tsx). NOT the big gradient PageHeader used elsewhere
 * in the app.
 *
 * @example
 * // List page header (Users.tsx / Teams.tsx / Agencies.tsx pattern)
 * <AdminPageHeader
 *   title="Users"
 *   subtitle="Manage all platform users"
 *   action={<Button onClick={() => setShowCreateModal(true)}>Create User</Button>}
 * />
 *
 * @example
 * // Detail page header with back link (UserDetail.tsx pattern)
 * <AdminPageHeader
 *   title={user.fullName || 'No name'}
 *   subtitle={user.email}
 *   backTo="/admin/users"
 *   backLabel="Back to Users"
 *   action={
 *     <div className="flex gap-3">
 *       <Button variant="secondary">Edit Profile</Button>
 *       <Button variant="danger">Delete User</Button>
 *     </div>
 *   }
 * />
 *
 * @example
 * // Detail page header with a leading logo avatar (TeamDetail.tsx pattern)
 * <AdminPageHeader
 *   title={team.name}
 *   subtitle={team.email}
 *   backTo="/admin/teams"
 *   backLabel="Back to Teams"
 *   leading={team.logo ? (
 *     <img src={team.logo} alt={team.name} className="w-10 h-10 rounded-full object-cover" />
 *   ) : undefined}
 * />
 */
export const AdminPageHeader = ({
  title,
  subtitle,
  action,
  backTo,
  backLabel = 'Back',
  leading,
  className = '',
  documentTitle,
}: AdminPageHeaderProps) => {
  // Set browser tab title if provided. When absent, resolves to the same
  // fallback ('') the hook uses internally, which matches the app default
  // title in index.html — so existing callers see no change.
  useDocumentTitle(documentTitle || '');

  return (
    <div className={cn(className)}>
      {backTo && (
        <Link
          to={backTo}
          className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 mb-4"
        >
          <Icon name={ICONS.ARROW_LEFT} size="sm" />
          {backLabel}
        </Link>
      )}
      <div className="flex items-center justify-between">
        <div className={leading ? 'flex items-center gap-4' : undefined}>
          {leading}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
            {subtitle && <p className="text-gray-500 mt-1">{subtitle}</p>}
          </div>
        </div>
        {action && <div className="flex items-center gap-3">{action}</div>}
      </div>
    </div>
  );
};

export default AdminPageHeader;
